import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import Dropdown from '@lib/inputs/Dropdown';
import Input from '@lib/inputs/Input';
import RadioButtons from '@lib/inputs/RadioButtons';

import { HelpAssistance } from '../constants/voterRegistrationConstants';
import { GridStyledWrapper, StyledRadioButton } from '../voterRegistration.styled';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import useVoterRegistration from '../useVoterRegistration';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';

function VotingAssistanceSection() {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<IVoterRegistrationFormValues>();
    const intl = useIntl();
    const { VOTER_REGISTRATION_CHOICES } = useVoterRegistration();
    const helpRequired = watch('votingAssistance.helpRequired');

    const handleFieldChange = (name: string, value: any) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    return (
        <div id="votingAssistance-section">
            <FormattedMessage id="voterRegistration.form.votingAssistance.header" tagName="h2" />
            <GridStyledWrapper maxWidth="50%">
                <span className="gi-radio_buttons">
                    <FormattedMessage id="voterRegistration.form.votingAssistance.helpRequired" />
                </span>
                <StyledRadioButton>
                    <RadioButtons
                        legend={{
                            legendText: intl.formatMessage({
                                id: 'voterRegistration.form.votingAssistance.helpRequired',
                            }),
                        }}
                        label={{
                            isLabelVisible: false,
                            ulClass: ['d-flex gap-5'],
                        }}
                        inputData={{
                            name: 'votingAssistance.helpRequired',
                            id: 'votingAssistance.helpRequired',
                            isInputRequired: true,
                            options: VOTER_REGISTRATION_CHOICES.YES_NO,
                            type: 'radio_buttons',
                            value: helpRequired,
                        }}
                        inputActions={{
                            handleFieldChange: val => {
                                handleFieldChange('votingAssistance.helpRequired', val);
                                handleFieldChange('votingAssistance.kindOfAssistance', '');
                                handleFieldChange('votingAssistance.preferredLanguage', '');
                            }
                        }}
                    />
                </StyledRadioButton>
            </GridStyledWrapper>
            <VoterRegistrationFormError
                name="votingAssistance.helpRequired"
                hasError={!!errors?.votingAssistance?.helpRequired}
                messageId={errors?.votingAssistance?.helpRequired?.message}
            />
            <ConditionalRendering isTrue={Boolean(helpRequired)}>
                <GridStyledWrapper>
                    <div>
                        <Dropdown
                            label={{
                                text: intl.formatMessage({
                                    id: 'voterRegistration.form.votingAssistance.kindOfAssistance',
                                }),
                            }}
                            inputData={{
                                id: 'votingAssistance.kindOfAssistance',
                                name: 'votingAssistance.kindOfAssistance',
                                isInputRequired: true,
                                value: watch('votingAssistance.kindOfAssistance'),
                                options: VOTER_REGISTRATION_CHOICES.HELP_ASSISTANCE,
                            }}
                            inputActions={{
                                handleFieldChange: val => {
                                    handleFieldChange('votingAssistance.kindOfAssistance', val);
                                    handleFieldChange('votingAssistance.preferredLanguage', '');
                                },
                            }}
                        />
                        <VoterRegistrationFormError
                            name="votingAssistance.kindOfAssistance"
                            hasError={!!errors?.votingAssistance?.kindOfAssistance}
                            messageId={errors?.votingAssistance?.kindOfAssistance?.message}
                        />
                    </div>
                    <ConditionalRendering
                        isTrue={watch('votingAssistance.kindOfAssistance') === HelpAssistance.LimitedEnglish}
                    >
                        <div className="empty" />
                        <div>
                            <Input
                                label={{
                                    text: intl.formatMessage({
                                        id: 'voterRegistration.form.votingAssistance.preferredLanguage',
                                    }),
                                }}
                                inputData={{
                                    id: 'votingAssistance.preferredLanguage',
                                    name: 'votingAssistance.preferredLanguage',
                                    isInputRequired: true,
                                    value: watch('votingAssistance.preferredLanguage'),
                                }}
                                inputActions={{
                                    handleFieldChange: val =>
                                        handleFieldChange('votingAssistance.preferredLanguage', val),
                                }}
                            />
                            <VoterRegistrationFormError
                                name="votingAssistance.preferredLanguage"
                                hasError={!!errors?.votingAssistance?.preferredLanguage}
                                messageId={errors?.votingAssistance?.preferredLanguage?.message}
                            />
                        </div>
                    </ConditionalRendering>
                </GridStyledWrapper>
            </ConditionalRendering>
        </div>
    );
}

export default VotingAssistanceSection;
